import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, FileText, CheckCircle2, AlertCircle } from 'lucide-react';

import { toast } from 'react-toastify';
import LabServices from '../../../shared/ApiServices/LabServices';
import { formatNameTitleCase } from '../../../shared/utils/nameUtils';

import { OrderDetailSkeleton } from '../../../shared/custom-ui/skeleton/Lab';
import { OrderWorkflowList } from './OrderWorkflowList';
import { StatusBadge, PriorityBadge } from '../OrderManagement';
import { Button, Dialog, Dropdown } from '../../../shared/custom-ui';
import PathologyUploadModal from '../../Pathology/PathologyUploadModal';

import {
  extractLabOrderId,
  extractPatientName,
  extractOrderStatus,
  extractLabInvoiceNumber,
  formatLabDate,
  ORDER_STATUS_META,
  pickLabColor,
  getInitials,
  type LabOrder,
  extractOrderPriority,
  extractLabSamplesList,
  type LabSample,
  normalizeSampleStatus,
  resolveLabStatus,
} from '../labUtils';

type StepState = 'done' | 'current' | 'pending';

const OrderJourney: React.FC<{ order: LabOrder; samples: LabSample[] }> = ({ order, samples }) => {
  const orderStatus = extractOrderStatus(order);

  if (orderStatus === 'cancelled') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50/50 px-3 py-2">
        <AlertCircle
          className="h-3.5 w-3.5 flex-shrink-0 text-red-600"
          strokeWidth={2}
        />
        <p className="m-0 text-[12px] font-bold text-red-600">
          Order cancelled — no further processing.
        </p>
      </div>
    );
  }

  const sampleStatuses = samples.map(normalizeSampleStatus);
  const collectedCount = sampleStatuses.filter(s => s !== 'pending' && s !== 'rejected').length;
  const totalSamples = samples.length;
  const anyPastPending = totalSamples > 0 && collectedCount > 0;
  const allPastPending = totalSamples > 0 && collectedCount === totalSamples;
  const reportReadySignal =
    orderStatus === 'completed' ||
    (order.tests ?? []).some(t => {
      const rs = (t as any).reportStatus ?? (t as any).report_status;
      return rs === 'completed' || rs === 'approved';
    });

  const effectiveJourneyLabStatus = resolveLabStatus(order);
  const hasCollectedSlide =
    effectiveJourneyLabStatus !== '' &&
    effectiveJourneyLabStatus !== 'PENDING' &&
    effectiveJourneyLabStatus !== 'WAITING_FOR_LAB_REPORT';

  const sampleDone = allPastPending || hasCollectedSlide;
  const sampleStarted = anyPastPending || sampleDone;
  const processingDone = sampleDone && (orderStatus === 'completed' || reportReadySignal);
  const processingStarted = sampleDone && orderStatus === 'processing';
  const reportDone = processingDone && reportReadySignal;

  const steps: { key: string; label: string; sub?: string; state: StepState }[] = [
    { key: 'placed', label: 'Order Placed', state: 'done' },
    {
      key: 'sample',
      label: 'Sample Collected',
      sub:
        totalSamples > 0
          ? `${sampleDone ? totalSamples : collectedCount}/${totalSamples}`
          : undefined,
      state: sampleDone ? 'done' : sampleStarted ? 'current' : 'pending',
    },
    {
      key: 'processing',
      label: 'Processing',
      state: processingDone ? 'done' : processingStarted ? 'current' : 'pending',
    },
    { key: 'report', label: 'Report Ready', state: reportDone ? 'done' : 'pending' },
  ];
  const activeStepIndex = Math.max(
    0,
    steps.reduce((latest, step, index) => (step.state !== 'pending' ? index : latest), 0)
  );
  const journeyComplete = reportDone;

  return (
    <div className="w-full">
      <div className="mb-2.5 flex items-center justify-between gap-4">
        <p className="m-0 text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--v2-text-muted)]">
          Order progress
        </p>
        <span
          className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-semibold ${
            journeyComplete
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-[var(--v2-primary-50,#eff6ff)] text-[var(--v2-primary-700,#034f9c)]'
          }`}
        >
          {journeyComplete ? '4 of 4 stages complete' : `Current: ${steps[activeStepIndex].label}`}
        </span>
      </div>

      <div className="v2-scrollbar flex items-center justify-start overflow-x-auto pb-1 lg:justify-center">
        {steps.map((step, i) => {
          const isActive = i === activeStepIndex;
          const isDone = step.state === 'done';
          const isPending = step.state === 'pending';
          const activeComplete = isActive && journeyComplete;

          return (
            <React.Fragment key={step.key}>
              <div
                className={`flex flex-shrink-0 items-center gap-2 rounded-lg px-2.5 py-2 ${
                  isActive
                    ? activeComplete
                      ? 'bg-emerald-50'
                      : 'bg-[var(--v2-primary-50,#eff6ff)]'
                    : ''
                }`}
                aria-current={isActive ? 'step' : undefined}
              >
                <div
                  className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-bold ${
                    isDone
                      ? activeComplete
                        ? 'bg-emerald-600 text-white'
                        : 'bg-[var(--v2-primary-500)] text-white'
                      : isActive
                        ? 'border-2 border-[var(--v2-primary-500)] bg-white text-[var(--v2-primary-600,#045eb8)]'
                        : 'border-2 border-[var(--v2-border-card)] bg-white text-[var(--v2-text-muted)]'
                  }`}
                >
                  {isDone ? (
                    <CheckCircle2
                      className="h-4 w-4"
                      strokeWidth={3}
                    />
                  ) : (
                    i + 1
                  )}
                </div>
                <div className="flex flex-col">
                  <span
                    className={`whitespace-nowrap text-[13px] font-semibold leading-tight ${
                      isPending && !isActive
                        ? 'text-[var(--v2-text-muted)]'
                        : 'text-[var(--v2-text-heading)]'
                    }`}
                  >
                    {step.label}
                  </span>
                  {isActive && (
                    <span
                      className={`text-[11px] font-medium ${
                        activeComplete ? 'text-emerald-700' : 'text-[var(--v2-primary-600,#045eb8)]'
                      }`}
                    >
                      {activeComplete
                        ? 'Complete'
                        : step.sub
                          ? `${step.sub} samples · Current stage`
                          : 'Current stage'}
                    </span>
                  )}
                </div>
              </div>

              {i < steps.length - 1 && (
                <div
                  className={`mx-1 h-[2px] w-10 flex-shrink-0 rounded-full ${
                    i < activeStepIndex
                      ? 'bg-[var(--v2-primary-500)]'
                      : 'bg-[var(--v2-border-subtle)]'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export const OrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId') ?? '';

  const [order, setOrder] = useState<LabOrder | null>(null);
  const [samples, setSamples] = useState<LabSample[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const mountedRef = useRef(true);

  const loadOrder = useCallback(async () => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [res, sm] = await Promise.all([
        LabServices.getOrder(orderId),
        LabServices.getSamplesForOrder(orderId).catch(() => []),
      ]);
      const orderData: LabOrder = (res as any)?.order ?? (res as any)?.data ?? res;
      // The order record's own `tests` (testId/testName) and the endpoint's top-level
      // `tests` (per-result reportStatus/resultId, keyed by SK "TEST#<testId>") can diverge —
      // a test can have a result in progress before the order record reflects it. Merge by
      // testId instead of preferring one array wholesale, so no test's status goes missing.
      const topLevelTests: any[] = (res as any)?.tests ?? [];
      const orderTests: any[] = orderData?.tests ?? [];
      const statusByTestId = new Map<string, any>();
      topLevelTests.forEach(t => {
        const tid = t.testId ?? t.test_id ?? String(t.SK ?? '').replace(/^TEST#/, '');
        if (tid) statusByTestId.set(tid, t);
      });
      const seen = new Set<string>();
      const testsData = [
        ...orderTests.map(t => {
          const tid = t.testId ?? t.test_id ?? '';
          seen.add(tid);
          const s = statusByTestId.get(tid);
          return s
            ? {
                ...t,
                reportStatus: s.reportStatus ?? s.report_status ?? t.reportStatus,
                resultId: s.resultId ?? s.result_id ?? t.resultId,
              }
            : t;
        }),
        ...topLevelTests
          .filter(t => {
            const tid = t.testId ?? t.test_id ?? String(t.SK ?? '').replace(/^TEST#/, '');
            return tid && !seen.has(tid);
          })
          .map(t => ({
            testId: t.testId ?? t.test_id ?? String(t.SK ?? '').replace(/^TEST#/, ''),
            testName: t.testName ?? t.test_name,
            reportStatus: t.reportStatus ?? t.report_status,
            resultId: t.resultId ?? t.result_id,
          })),
      ];
      if (mountedRef.current) {
        setOrder({ ...orderData, tests: testsData });
        setSamples(extractLabSamplesList(sm));
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to load order');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    mountedRef.current = true;
    loadOrder();
    return () => {
      mountedRef.current = false;
    };
  }, [loadOrder]);

  const handleUpdateStatus = async (newStatus: string) => {
    if (!order) return;
    setUpdatingStatus(true);
    setShowStatusMenu(false);
    try {
      const res: any = await LabServices.updateOrder(extractLabOrderId(order), {
        orderStatus: newStatus,
      });
      toast.success(res?.message ?? `Order moved to ${newStatus}`);
      loadOrder();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? 'Update failed');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const generateInvoicePdf = async () => {
    if (!order) return;
    setGeneratingPdf(true);
    try {
      const res = await LabServices.generateInvoicePdf(extractLabOrderId(order));
      const url =
        (res as any)?.invoiceUrl ||
        (res as any)?.invoice_url ||
        (res as any)?.url ||
        (res as any)?.pdfUrl ||
        (res as any)?.pdf_url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else if ((res as any)?.invoiceKey || (res as any)?.invoice_key) {
        const key = (res as any).invoiceKey ?? (res as any).invoice_key;
      } else {
        toast.info((res as any)?.message ?? 'Invoice generated. Check back shortly.');
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Invoice generation failed');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleCancel = async () => {
    if (!order) return;
    setCancelling(true);
    try {
      const res: any = await LabServices.cancelOrder(extractLabOrderId(order));
      toast.success(res?.message ?? 'Order cancelled');
      navigate('/lab/orders');
    } catch (err: any) {
      toast.error(err?.message ?? 'Cancel failed');
    } finally {
      setCancelling(false);
      setCancelConfirm(false);
    }
  };

  // ── Loading / empty states ────────────────────────────────────────────────
  if (loading) return <OrderDetailSkeleton />;

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <div className="mb-1 flex h-16 w-16 items-center justify-center rounded-full border border-[var(--v2-border-card)] bg-[var(--v2-bg-surface)]">
          <AlertCircle
            className="h-8 w-8 text-[var(--v2-text-muted)] opacity-50"
            strokeWidth={1.5}
          />
        </div>
        <h2 className="m-0 text-lg font-bold text-[var(--v2-text-heading)]">Order Not Found</h2>
        <p className="m-0 max-w-sm text-[13px] text-[var(--v2-text-secondary)]">
          {orderId
            ? 'The requested order could not be located in our system.'
            : 'No order ID was provided.'}
        </p>
        <Button
          variant="primary"
          onClick={() => navigate('/lab/orders')}
          leftIcon={
            <ChevronLeft
              className="h-full w-full"
              strokeWidth={2.5}
            />
          }
        >
          Back to Orders
        </Button>
      </div>
    );
  }

  const orderId_ = extractLabOrderId(order);
  const status = extractOrderStatus(order);

  const STATUS_TRANSITIONS: Record<string, string[]> = {
    pending: ['processing', 'completed'],
    appointment_scheduled: ['processing', 'completed'],
    processing: ['completed'],
    completed: [],
    cancelled: [],
  };
  const availableStatuses = STATUS_TRANSITIONS[status] ??
    STATUS_TRANSITIONS[order?.orderStatus ?? ''] ?? ['processing', 'completed'];

  // Appointment-sourced orders (e.g. pathology) may never carry billing fields — nothing to invoice.
  const hasInvoiceData = Boolean(
    extractLabInvoiceNumber(order) ||
      order.totalAmount ||
      order.total_amount ||
      order.netAmount ||
      order.subtotal
  );

  return (
    <div className="flex min-h-screen flex-col bg-[var(--v2-bg-base)] pb-8">
      {/* ── HEADER ── */}
      <div className="w-full rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)]">
        <div className="w-full px-5 py-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/lab/orders')}
                  className="flex h-9 w-9 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border border-[var(--v2-border-interactive)] bg-[var(--v2-bg-card)] text-[var(--v2-text-secondary)] transition-colors hover:bg-[var(--v2-bg-hover)] hover:text-[var(--v2-primary-600,#045eb8)]"
                  aria-label="Back to orders"
                >
                  <ChevronLeft
                    className="h-4 w-4"
                    strokeWidth={2.5}
                  />
                </button>

                <div
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-[14px] font-bold text-white"
                  style={{ background: pickLabColor(orderId_) }}
                >
                  {getInitials(extractPatientName(order))}
                </div>

                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="m-0 truncate text-[19px] font-bold leading-tight tracking-tight text-[var(--v2-text-heading)]">
                      {formatNameTitleCase(extractPatientName(order))}
                    </h1>
                    <StatusBadge status={status} />
                    <PriorityBadge priority={extractOrderPriority(order)} />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-[13px] font-medium text-[var(--v2-text-secondary)]">
                    {[
                      order.mrn && `MRN ${order.mrn}`,
                      (order.age || order.gender) &&
                        [order.age && `${order.age} years`, order.gender]
                          .filter(Boolean)
                          .join(' · '),
                      order.orderDate && `Ordered ${formatLabDate(order.orderDate)}`,
                    ]
                      .filter(Boolean)
                      .map((text, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="opacity-40">•</span>}
                          <span className="capitalize">{text}</span>
                        </React.Fragment>
                      ))}
                  </div>
                  <span className="text-[12px] font-medium text-[var(--v2-text-muted)]">
                    Order ID:{' '}
                    <span className="font-mono font-semibold">
                      #{extractLabInvoiceNumber(order) || orderId_.slice(-8)}
                    </span>
                  </span>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2 pl-12 lg:pl-0">
                <PathologyUploadModal
                  onUploadFinished={() => {
                    loadOrder();
                  }}
                  preselectedPatientId={
                    order.patientId
                      ? order.patientId.startsWith('PATIENT#')
                        ? order.patientId
                        : `PATIENT#${order.patientId}`
                      : undefined
                  }
                  preselectedPatientName={extractPatientName(order)}
                  preselectedOrderId={orderId_}
                />

                {hasInvoiceData && (
                  <Button
                    variant="outline"
                    size="small"
                    onClick={generateInvoicePdf}
                    loading={generatingPdf}
                    leftIcon={
                      !generatingPdf ? (
                        <FileText
                          className="h-full w-full"
                          strokeWidth={2}
                        />
                      ) : undefined
                    }
                  >
                    Invoice
                  </Button>
                )}

                {status !== 'cancelled' && availableStatuses.length > 0 && (
                  <Dropdown
                    isOpen={showStatusMenu}
                    toggleDropdown={() => setShowStatusMenu(s => !s)}
                    onClose={() => setShowStatusMenu(false)}
                    buttonLabel={updatingStatus ? 'Updating…' : 'Status'}
                    variant="outlined"
                    size="small"
                    placement="bottom-right"
                    options={availableStatuses.map(s => ({
                      value: s,
                      label: ORDER_STATUS_META[s as keyof typeof ORDER_STATUS_META]?.label ?? s,
                      onClick: () => handleUpdateStatus(s),
                      icon: (
                        <div
                          className="h-2 w-2 rounded-full"
                          style={{
                            background:
                              ORDER_STATUS_META[s as keyof typeof ORDER_STATUS_META]?.color ??
                              '#6B7280',
                          }}
                        />
                      ),
                    }))}
                  />
                )}

                {status !== 'cancelled' && status !== 'completed' && (
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => setCancelConfirm(true)}
                    className="!text-[var(--v2-text-secondary)] hover:!border-red-200 hover:!bg-red-50 hover:!text-red-600"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>

            <div className="border-t border-[var(--v2-border-subtle)] pt-3 pl-12 lg:pl-14">
              <OrderJourney
                order={order}
                samples={samples}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN LAYOUT — one unified workflow list: for each test, its specimen
                stage and result status side by side, with a single primary action for
                whatever's next (Register Specimen → Mark Collected → Send for Transit /
                Enter Result → Accession → Enter Result → View Result). ── */}
      <div className="flex w-full flex-col gap-4 pt-3">
        <OrderWorkflowList
          orderId={orderId_}
          order={order}
          samples={samples}
          onSampleChange={loadOrder}
        />
      </div>

      {/* Cancel confirm modal */}
      <Dialog
        isOpen={cancelConfirm}
        onClose={() => setCancelConfirm(false)}
        size="small"
        preventCloseOnOverlayClick
        showCloseButton={false}
      >
        <div className="-mt-2 flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
              <AlertCircle
                className="h-5 w-5 text-red-600"
                strokeWidth={2.5}
              />
            </div>
            <h2 className="m-0 text-[15px] font-bold text-[var(--v2-text-heading)]">
              Cancel this order?
            </h2>
          </div>
          <p className="m-0 text-[13px] font-medium leading-relaxed text-[var(--v2-text-secondary)]">
            Are you sure you want to cancel this order? This action cannot be undone and will halt
            any further processing.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setCancelConfirm(false)}
            >
              Keep Order
            </Button>
            <Button
              variant="danger"
              onClick={handleCancel}
              loading={cancelling}
            >
              Yes, Cancel Order
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default OrderDetail;
